import type { ReactNode } from 'react';

interface Column<T> {
  header: string;
  accessor: keyof T | string;
  render?: (value: unknown, row: T) => ReactNode;
  width?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  loading?: boolean;
  emptyMessage?: string;
  rowKey?: (row: T) => string;
}

function getNestedValue(obj: unknown, path: string): unknown {
  return path.split('.').reduce((curr: unknown, key) => {
    if (curr && typeof curr === 'object') {
      return (curr as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

export default function DataTable<T>({
  columns,
  data,
  onRowClick,
  loading = false,
  emptyMessage = 'No data',
  rowKey,
}: DataTableProps<T>) {
  if (loading) {
    return (
      <div className="table-container">
        <div className="table-loading">
          <span className="spinner" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="table-container">
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={String(col.accessor)} style={col.width ? { width: col.width } : undefined}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="table-empty">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, index) => (
              <tr
                key={rowKey ? rowKey(row) : index}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={onRowClick ? 'data-table__row--clickable' : undefined}
              >
                {columns.map((col) => {
                  const rawValue = getNestedValue(row, String(col.accessor));
                  return (
                    <td key={String(col.accessor)}>
                      {col.render ? col.render(rawValue, row) : String(rawValue ?? '')}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
